import React, { useState } from 'react'
import { ExpandMoreIcon } from '@resiliatech/birdsong'
import {
  Goal,
  ImpactDashboardActivity,
  UserSurveyPriority,
} from 'Generated/graphql-types'
import { Accordion, AccordionDetails, Grid } from '@mui/material'
import { toTitleCase } from 'Utils/formatters'
import { ArrowForward, FormatListBulleted } from '@material-ui/icons'
import { useHistory } from 'react-router'
import { UserSurveyBanner } from 'Components/Pages/NeedsAssessment/components/UserSurveyBanner/UserSurveyBanner'
import { WithWidgetLoading } from '../WithWidgetLoading'
import { Header, StyledAccordionSummary } from '../ImpactReportDashboard.styled'
import {
  PriorityProgressContainer,
  BannerContainer,
  PriorityCard,
  PriorityInfo,
  PriorityTitle,
  PriorityText,
} from './PriorityProgress.styled'
import { EmptyState } from '../EmptyState/EmptyState'

type PriorityProgressProps = {
  goals: Goal[]
  loading: boolean
  userResourceActivity: ImpactDashboardActivity[]
  userSurveyPriorities: Omit<
    UserSurveyPriority,
    'customPriorities' | 'userSurveyId' | 'UserSurveyPriorityTopic'
  >[]
}

export const PriorityProgress = ({
  goals,
  loading,
  userResourceActivity,
  userSurveyPriorities,
}: PriorityProgressProps) => {
  const initialExpansion = true
  const [isAccordionExpanded, setIsAccordionExpanded] =
    useState(initialExpansion)
  const history = useHistory()

  const getGoalsCreatedCount = (priorityName: string) =>
    goals.filter((goal) => goal.category === priorityName).length

  const calculateConfidenceLevelPercentage = (rating: number): number => {
    const percentage = ((rating - 1) / 4) * 80 + 20
    return Math.round(percentage)
  }

  const getTasksProgress = (priorityName: string) => {
    const priorityGoals = goals.filter((goal) => goal.category === priorityName)
    const totalTasks = priorityGoals.reduce(
      (total, goal) => total + goal.ChecklistItem.length,
      0
    )
    const completedTasks = priorityGoals.reduce(
      (completed, goal) =>
        completed +
        goal.ChecklistItem.filter((item) => item.isCompleted).length,
      0
    )
    return `${completedTasks}/${totalTasks}`
  }

  const userResourceActivityByTopic: Record<string, number> =
    userResourceActivity.reduce((acc, activity) => {
      const topic = activity.resource.nonprofitTopic
      if (acc[topic]) acc[topic] += 1
      else acc[topic] = 1
      return acc
    }, {} as Record<string, number>)

  const handleAddPriority = () => {
    history.push('/get-started?newSurvey=true')
  }

  const isLoading = !userSurveyPriorities || loading
  const isEmptyState = !isLoading && !userSurveyPriorities.length

  return (
    <WithWidgetLoading
      loading={isLoading}
      width='100%'
      height={isLoading ? '240px' : 'auto'}
    >
      <Accordion
        disableGutters
        defaultExpanded={initialExpansion}
        sx={{ margin: 0 }}
        elevation={0}
        onChange={() => setIsAccordionExpanded((prev) => !prev)}
      >
        <StyledAccordionSummary
          disabled={isEmptyState}
          expandIcon={isEmptyState ? null : <ExpandMoreIcon />}
        >
          <Header>Priorities</Header>
        </StyledAccordionSummary>
        {isEmptyState ? (
          <EmptyState
            icon={FormatListBulleted}
            title='No Priorities Set'
            description='Set your priorities by completing the Needs Assessment.'
            buttonText='Start Assessment'
            endIcon={ArrowForward}
            onButtonClick={handleAddPriority}
          />
        ) : (
          <AccordionDetails
            sx={{
              padding: 0,
              paddingTop: isAccordionExpanded ? '10px' : 0,
              transition: 'all 0.3s',
            }}
          >
            <PriorityProgressContainer>
              <Grid container spacing={2}>
                {userSurveyPriorities.map(
                  ({ id, category, confidenceLevel }) => {
                    const resourceCount =
                      userResourceActivityByTopic?.[toTitleCase(category)] ?? 0
                    return (
                      <Grid
                        item
                        xs={12}
                        sm={userSurveyPriorities.length === 2 ? 6 : 4}
                        key={id}
                        style={{ display: 'flex' }}
                      >
                        <PriorityCard>
                          <PriorityTitle>{toTitleCase(category)}</PriorityTitle>
                          <PriorityInfo>
                            <PriorityText>
                              Confidence Level:{' '}
                              {calculateConfidenceLevelPercentage(
                                confidenceLevel
                              )}
                              %
                            </PriorityText>
                            <PriorityText>
                              Goals Created: {getGoalsCreatedCount(category)}
                            </PriorityText>
                            <PriorityText>
                              Tasks: {getTasksProgress(category)}
                            </PriorityText>
                            <PriorityText>
                              Academy Resources Viewed: {resourceCount}
                            </PriorityText>
                          </PriorityInfo>
                        </PriorityCard>
                      </Grid>
                    )
                  }
                )}
              </Grid>
            </PriorityProgressContainer>
          </AccordionDetails>
        )}
      </Accordion>
      <BannerContainer>
        <UserSurveyBanner />
      </BannerContainer>
    </WithWidgetLoading>
  )
}
